'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { getServiceLogs, type ServiceLogFilters } from '@/lib/api/client/settings'
import type { ServiceLogCategoryType, ServiceLogItemType, ServiceLogReasonCodeType, ServiceLogSeverityType, ServiceLogSourceType } from '@/lib/api/schemas/settings.schema'

const severityLabels: Record<ServiceLogSeverityType, string> = { info: 'Информация', warning: 'Предупреждение', error: 'Ошибка' }
const categoryLabels: Record<ServiceLogCategoryType, string> = { orders: 'Заказы', cdek: 'СДЭК', messages: 'Сообщения', notifications: 'Уведомления', analytics: 'Аналитика', stockGuard: 'Защита остатков', wildberries: 'Wildberries', system: 'Система', unknown: 'Требует проверки' }
const sourceLabels: Record<ServiceLogSourceType, string> = { automatic: 'Автоматически', manual: 'Вручную', system: 'Система' }
const severityClasses: Record<ServiceLogSeverityType, string> = { info: 'bg-bone text-ink-2', warning: 'bg-gold/15 text-gold-deep', error: 'bg-st-unpaid/10 text-st-unpaid' }
const reasonLabels: Partial<Record<ServiceLogReasonCodeType, string>> = {
  webhook_subscription_repaired: 'Подписка СДЭК автоматически создана заново; ожидается новая доставка событий.',
  stock_protection_identity_conflict: 'Защита остатка по заказу Яндекс Маркета противоречит сохранённому движению или резерву; строка оставлена для проверки.',
  poller_fallback_not_fresh: 'Резервный опрос не запущен: данные ещё недостаточно свежие.',
  provider_data_unavailable: 'Данные провайдера временно недоступны.',
  provider_error: 'Сервис провайдера вернул ошибку.',
  callback_provider_read_failed: 'Не удалось прочитать данные провайдера; обработка будет повторена.',
  callback_work_pending: 'Операция ожидает повторной обработки.',
  callback_retry_exhausted: 'Повторные попытки исчерпаны; требуется проверка.',
  automation_disabled: 'Автоматизация отключена.',
  dispatch_not_configured: 'Отправка не настроена.',
  notification_disabled: 'Уведомления отключены.',
  notification_event_disabled: 'Уведомление для этого события отключено.',
  notification_not_configured: 'Telegram не настроен для отправки уведомлений.',
  notification_provider_refused: 'Telegram отклонил уведомление.',
  notification_provider_unavailable: 'Telegram временно недоступен или вернул ошибку.',
  notification_retry_exhausted: 'Карточка администратору не ушла: попытки отправки исчерпаны.',
  notification_unexpected_outcome: 'Telegram вернул непредвиденный результат; требуется проверка.',
  already_claimed: 'Операция уже обрабатывается или завершена.',
  already_sent: 'Сообщение уже отправлено.',
  callback_provider_refused: 'Провайдер отказал в доступе к заказу; обработка события прекращена.',
  callback_provider_response_invalid: 'Ответ провайдера по заказу не удалось разобрать; требуется проверка.',
  callback_buyer_detail_unavailable: 'Заказ сохранён, но данные покупателя из кампании получить не удалось.',
  callback_automation_waiting: 'Сообщение покупателю пока не отправлено; обработка будет повторена.',
  order_observation_stale: 'Сохранённые данные заказа новее полученных; запись не изменена.',
  order_generation_drift: 'Данные заказа расходятся в пределах одной версии провайдера; требуется проверка.',
  orders_list_served_stale: 'Маркет не отдал список заказов; показан последний сохранённый список с пометкой «устарели».',
  automation_stuck: 'Отправка сообщения покупателю прервалась и ждёт решения оператора.',
  automation_recovered: 'Отправка сообщений покупателям восстановлена.',
  claim_generation_conflict: 'Решение по заказу изменилось; прежняя отправка остановлена.',
  claim_content_unverifiable: 'Текст сообщения не совпадает с сохранённым; отправка остановлена.',
  claim_no_longer_carried: 'Обязательство отправить сообщение больше не действует.',
  automation_disabled_during_resume: 'Автоматизация отключена во время продолжения; сообщение не отправлено.',
  operator_marked_sent: 'Оператор подтвердил отправку сообщения покупателю.',
  operator_closed_unsent: 'Оператор закрыл обращение без отправки сообщения.',
  operator_reopened_unsent: 'Оператор открыл повторную отправку сообщения покупателю.',
  poll_cycle_completed: 'Цикл проверки статусов завершён.',
  candidate_capacity_exceeded: 'Отправлений больше, чем помещается в один цикл; остальные проверятся позже.',
  unknown_order_status: 'Провайдер вернул неизвестный статус.',
  malformed_provider_payload: 'Провайдер прислал некорректные данные.',
  handoff_record_missing: 'Подтверждение передачи отправления не найдено; требуется проверка.',
  carrier_waybill_retired: 'Накладная удалена в СДЭК и больше не проверяется; действующие накладные заказа доступны для печати и сообщений.',
  poller_fallback_bootstrap: 'Резервный опрос только что начал отсчёт; прошлые заказы не обрабатываются.',
  poller_fallback_not_eligible: 'Заказ не подходит для резервной обработки.',
  callback_silence: 'Уведомления от провайдера не приходили дольше допустимого.',
  callback_recovered: 'Уведомления от провайдера снова приходят.',
  report_unavailable: 'Отчёт провайдера недоступен; требуется проверка.',
  report_recovered: 'Отчёт провайдера снова доступен.',
  cdek_link_not_yet_available: 'Связь отправления с заказом пока не подтверждена; проверка будет повторена.',
  cdek_link_ambiguous: 'Связь отправления с заказом противоречива и требует проверки.',
  cdek_link_resolved: 'Связь отправления с заказом подтверждена.',
  cdek_link_retry_exhausted: 'Связь отправления с заказом не подтверждена после ограниченных попыток.',
  notification_event_not_configured: 'Telegram не настроен для этого типа уведомлений.',
  callback_work_succeeded: 'Событие провайдера успешно обработано.',
  scheduled_run_failed: 'Плановый запуск прервался ошибкой на нашей стороне; данные за этот запуск не обновлены.',
  database_lock_timeout: 'Запись отложена: нужная строка в базе данных занята; повторная попытка выполнится позже.',
  database_statement_timeout: 'Запись отложена: запрос к базе данных превысил лимит времени; повторная попытка выполнится позже.',
  database_socket_deadline: 'База данных не отвечает дольше своего лимита; новые записи временно отклоняются, чтение работает.',
  release_scheduler_commit_failed: 'Выпуск принят, но состояние отката планировщика осталось на сервере; нужна проверка.',
  release_observability_not_recreated: 'Выпуск принят, но стек технических логов не был пересоздан; на выпуск это не влияет.',
  economics_input_zero_coverage: 'Сбор цен не получил ни одного значения для одного из входов экономики; подробности — в строке покрытия операции.',
  economics_input_stale: 'Себестоимость из 1С давно не обновлялась; синхронизатор 1С нужно проверить.',
  onec_sync_run_failed: 'Синкер 1С на gold01 не смог выполнить синхронизацию; подробности — в разделе «Синхронизация с 1С».',
}

function occurredAt(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Riga' }).format(new Date(value))
}

export type ServiceLogRecurrence = {
  item: ServiceLogItemType
  count: number
  firstOccurredAt: string
  lastOccurredAt: string
}

function sameIncident(left: ServiceLogItemType, right: ServiceLogItemType): boolean {
  return left.severity === right.severity
    && left.category === right.category
    && left.source === right.source
    && left.summary === right.summary
    && left.reasonCode === right.reasonCode
}

/**
 * Collapse an adjacent run of identical incidents into one counted row.
 *
 * The previous filter dropped every repeat and kept only the newest, so N
 * identical failures rendered as one and the operator could not tell a single
 * incident from a storm. Occurrence time is part of the answer, not noise: each
 * row keeps the whole span it stands for.
 */
export function collapseRepeatedItems(items: ServiceLogItemType[]): ServiceLogRecurrence[] {
  const groups: ServiceLogRecurrence[] = []
  for (const item of items) {
    const current = groups[groups.length - 1]
    if (current !== undefined && sameIncident(current.item, item)) {
      current.count += 1
      // Compared as instants, not as strings: the same instant has more than one
      // ISO spelling, and a lexicographic winner would silently be the wrong one.
      if (Date.parse(item.occurredAt) < Date.parse(current.firstOccurredAt)) current.firstOccurredAt = item.occurredAt
      if (Date.parse(item.occurredAt) > Date.parse(current.lastOccurredAt)) current.lastOccurredAt = item.occurredAt
      continue
    }
    groups.push({ item, count: 1, firstOccurredAt: item.occurredAt, lastOccurredAt: item.occurredAt })
  }
  return groups
}

export function ServiceLogSettings() {
  const [filters, setFilters] = useState<ServiceLogFilters>({})
  const [items, setItems] = useState<ServiceLogItemType[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [loadingMore, setLoadingMore] = useState(false)

  // The raw page order is the state; collapsing is a view over it. Collapsing
  // into state instead lost the earlier occurrences a later page needs to count.
  const groups = useMemo(() => collapseRepeatedItems(items), [items])

  const load = useCallback(async (cursor?: string) => {
    if (cursor) setLoadingMore(true)
    else setState('loading')
    try {
      const page = await getServiceLogs(filters, cursor)
      setItems((current) => (cursor ? [...current, ...page.items] : page.items))
      setNextCursor(page.nextCursor)
      setHasMore(page.hasMore)
      setState('ready')
    } catch {
      if (!cursor) setItems([])
      setState('error')
    } finally {
      setLoadingMore(false)
    }
  }, [filters])

  useEffect(() => {
    let active = true
    setState('loading')
    getServiceLogs(filters)
      .then((page) => {
        if (!active) return
        setItems(page.items)
        setNextCursor(page.nextCursor)
        setHasMore(page.hasMore)
        setState('ready')
      })
      .catch(() => { if (active) { setItems([]); setState('error') } })
    return () => { active = false }
  }, [filters])

  const change = (key: keyof ServiceLogFilters, value: string) => setFilters((current) => ({ ...current, [key]: value || undefined }))

  return (
    <section aria-labelledby="service-log-title">
      <div>
        <h2 id="service-log-title" className="text-[20px] font-semibold text-ink">Журналы сервисов</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">Безопасная операционная сводка без идентификаторов, содержимого сообщений и данных провайдеров.</p>
      </div>
      <div className="mt-5 grid gap-3 rounded-lg border border-rule bg-paper p-4 sm:grid-cols-3">
        <Filter label="Важность" value={filters.severity ?? ''} onChange={(value) => change('severity', value)} options={severityLabels} />
        <Filter label="Категория" value={filters.category ?? ''} onChange={(value) => change('category', value)} options={categoryLabels} />
        <Filter label="Источник" value={filters.source ?? ''} onChange={(value) => change('source', value)} options={sourceLabels} />
      </div>
      {state === 'loading' && <div role="status" className="mt-5 rounded-lg border border-rule bg-paper p-5 text-[13px] text-ink-muted">Загрузка журнала…</div>}
      {state === 'error' && <div role="alert" className="mt-5 rounded-lg border border-st-unpaid/25 bg-paper p-5 text-[13px] text-st-unpaid">Не удалось загрузить журнал сервисов. <button type="button" className="ml-1 underline" onClick={() => void load()}>Повторить</button></div>}
      {state === 'ready' && items.length === 0 && <div className="mt-5 rounded-lg border border-rule bg-paper p-5 text-[13px] text-ink-muted">Событий по выбранным фильтрам нет.</div>}
      {state === 'ready' && groups.length > 0 && (
        <>
          <ol className="mt-5 space-y-2">
            {groups.map((group, index) => (
              <li key={`${group.lastOccurredAt}-${group.firstOccurredAt}-${index}`} className="rounded-lg border border-rule bg-paper p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-sm px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${severityClasses[group.item.severity]}`}>{severityLabels[group.item.severity]}</span>
                  <span className="text-[11px] text-ink-muted">{categoryLabels[group.item.category]}</span>
                  <span aria-hidden="true" className="text-rule-strong">·</span>
                  <span className="text-[11px] text-ink-muted">{sourceLabels[group.item.source]}</span>
                  {group.count > 1 && <span className="rounded-sm bg-bone px-2 py-1 text-[10px] font-semibold tabular-nums text-ink-2">{`Повторов: ${group.count}`}</span>}
                  <span className="ml-auto text-[11px] tabular-nums text-ink-faint">
                    {group.count > 1 && <><time dateTime={group.firstOccurredAt}>{occurredAt(group.firstOccurredAt)}</time>{' — '}</>}
                    <time dateTime={group.lastOccurredAt}>{occurredAt(group.lastOccurredAt)}</time>
                  </span>
                </div>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-2">{group.item.summary}</p>
                {group.item.reasonCode && <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">Причина: {reasonLabels[group.item.reasonCode] ?? 'Требуется проверка по закрытому коду причины.'}</p>}
              </li>
            ))}
          </ol>
          {hasMore && nextCursor && <button type="button" className="btn btn-ghost mt-4 w-full" disabled={loadingMore} onClick={() => void load(nextCursor)}>{loadingMore ? 'Загрузка…' : 'Загрузить ещё'}</button>}
        </>
      )}
    </section>
  )
}

function Filter<T extends string>({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Record<T, string> }) {
  const entries = Object.entries(options) as Array<[T, string]>
  return <label className="text-[11px] font-semibold text-ink-2">{label}<select className="input mt-1.5 w-full bg-ivory" value={value} onChange={(event) => onChange(event.target.value)}><option value="">Все</option>{entries.map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select></label>
}
